import React, { useState } from "react";
import {
    HiArrowRight,
    HiArrowLeft,
    HiMapPin,
    HiCheckCircle,
    HiLockClosed,
    HiChevronDown,
    HiChevronUp,
} from "react-icons/hi2";
import {
    MapContainer,
    TileLayer,
    Marker,
    useMapEvents,
    useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Button from "../../common/Button/Button";
import "./Step1.scss";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const LocationMarker = ({ position, setPosition }) => {
    useMapEvents({
        click(e) {
            setPosition([e.latlng.lat, e.latlng.lng]);
        },
    });

    return position ? <Marker position={position} /> : null;
};

const MapUpdater = ({ center }) => {
    const map = useMap();

    React.useEffect(() => {
        if (center) {
            map.setView(center, 13, {
                animate: true,
                duration: 1,
            });
        }
    }, [center, map]);

    return null;
};

const Step1 = ({ onNext, onBack, initialData }) => {
    const [formData, setFormData] = useState(
        initialData || {
            projectName: "",
            contactName: "",
            companyName: "",
            phone: "",
            location: "",
            coordinates: null,
            projectArea: "",
            scanningPurpose: [],
            otherPurpose: "",
            scopeOfWork: "",
        }
    );

    const getInitialCompletedSections = () => {
        const completed = [];
        const data = initialData || formData;

        if (data.projectName?.trim()) completed.push(1);
        if (data.contactName?.trim()) completed.push(2);
        if (data.location?.trim()) completed.push(3);
        if (data.projectArea?.trim()) completed.push(4);
        if (data.scanningPurpose?.length > 0) completed.push(5);
        if (data.scopeOfWork?.trim()) completed.push(6);

        return completed;
    };

    const [openSection, setOpenSection] = useState(1);
    const [completedSections, setCompletedSections] = useState(
        getInitialCompletedSections()
    );
    const [errors, setErrors] = useState({});
    const [mapPosition, setMapPosition] = useState(
        initialData?.coordinates || [40.4168, -3.7038]
    );
    const [markerPosition, setMarkerPosition] = useState(
        initialData?.coordinates || null
    );
    const [isSearching, setIsSearching] = useState(false);

    const searchLocation = async (address) => {
        if (!address.trim() || address.length < 3) return;

        setIsSearching(true);
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?` +
                    `format=json` +
                    `&q=${encodeURIComponent(address)}` +
                    `&countrycodes=es` + // Solo resultados de España
                    `&limit=1` +
                    `&addressdetails=1` +
                    `&accept-language=es`, // Idioma español
                {
                    headers: {
                        "User-Agent": "Atlyon-App/1.0",
                    },
                }
            );
            const data = await response.json();

            if (data && data.length > 0) {
                const { lat, lon } = data[0];
                const newPosition = [parseFloat(lat), parseFloat(lon)];
                setMapPosition(newPosition);
                setMarkerPosition(newPosition);
                setFormData((prev) => ({
                    ...prev,
                    coordinates: newPosition,
                }));
            }
        } catch (error) {
            console.error("Error buscando ubicación:", error);
        } finally {
            setIsSearching(false);
        }
    };

    // Geocoding inverso: coordenadas → dirección
    const reverseGeocode = async (lat, lng) => {
        setIsSearching(true);
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?` +
                    `format=json` +
                    `&lat=${lat}` +
                    `&lon=${lng}` +
                    `&accept-language=es`,
                {
                    headers: {
                        "User-Agent": "Atlyon-App/1.0",
                    },
                }
            );
            const data = await response.json();

            if (data && data.display_name) {
                setFormData((prev) => ({
                    ...prev,
                    location: data.display_name,
                    coordinates: [lat, lng],
                }));
            }
        } catch (error) {
            console.error("Error en geocoding inverso:", error);
        } finally {
            setIsSearching(false);
        }
    };

    const handleLocationChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }

        setCompletedSections((prev) => {
            if (value.trim() && !prev.includes(3)) {
                return [...prev, 3];
            } else if (!value.trim() && prev.includes(3)) {
                return prev.filter((s) => s !== 3);
            }
            return prev;
        });

        if (value.length > 2) {
            clearTimeout(window.searchTimeout);
            window.searchTimeout = setTimeout(() => {
                searchLocation(value);
            }, 1000);
        }
    };

    const scanningPurposes = [
        "Crear planos as-built / modelo 3D",
        "Planificación espacial / Estudio de área para inmobiliaria",
        "Detección de colisiones de nuevo diseño vs. estructuras existentes",
        "Escaneo para prefabricación y planos de taller",
        "Verificación de construcción / seguimiento de progreso",
        "Validación de planitud del piso",
        "Localización de servicios subterráneos",
        "Planificación urbana",
        "Gestión de instalaciones",
        "Marketing / Tours virtuales",
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }

        updateCompletedSections(name, value);
    };

    const updateCompletedSections = (fieldName, fieldValue) => {
        // Mapeo de campos a números de sección
        const fieldToSection = {
            projectName: 1,
            contactName: 2,
            companyName: 2,
            phone: 2,
            location: 3,
            projectArea: 4,
            otherPurpose: 5,
            scopeOfWork: 6,
        };

        const sectionNumber = fieldToSection[fieldName];
        if (!sectionNumber) return;

        setCompletedSections((prev) => {
            const isNowComplete = isSectionComplete(sectionNumber);

            if (isNowComplete && !prev.includes(sectionNumber)) {
                return [...prev, sectionNumber];
            } else if (!isNowComplete && prev.includes(sectionNumber)) {
                return prev.filter((s) => s !== sectionNumber);
            }

            return prev;
        });
    };

    const handleCheckboxChange = (purpose) => {
        setFormData((prev) => {
            const newPurposes = prev.scanningPurpose.includes(purpose)
                ? prev.scanningPurpose.filter((p) => p !== purpose)
                : [...prev.scanningPurpose, purpose];

            setCompletedSections((prevCompleted) => {
                if (newPurposes.length > 0 && !prevCompleted.includes(5)) {
                    return [...prevCompleted, 5];
                } else if (
                    newPurposes.length === 0 &&
                    prevCompleted.includes(5)
                ) {
                    return prevCompleted.filter((s) => s !== 5);
                }
                return prevCompleted;
            });

            return { ...prev, scanningPurpose: newPurposes };
        });

        if (errors.scanningPurpose) {
            setErrors((prev) => ({ ...prev, scanningPurpose: null }));
        }
    };

    const handleMapClick = (position) => {
        setMarkerPosition(position);
        setMapPosition(position);
        reverseGeocode(position[0], position[1]);
    };

    const validateSection = (sectionNumber) => {
        const newErrors = {};

        switch (sectionNumber) {
            case 1:
                if (!formData.projectName.trim())
                    newErrors.projectName = "Campo requerido";
                break;
            case 2:
                if (!formData.contactName.trim())
                    newErrors.contactName = "Campo requerido";
                break;
            case 3:
                if (!formData.location.trim())
                    newErrors.location = "Campo requerido";
                break;
            case 4:
                if (!formData.projectArea.trim())
                    newErrors.projectArea = "Campo requerido";
                break;
            case 5:
                if (formData.scanningPurpose.length === 0)
                    newErrors.scanningPurpose =
                        "Selecciona al menos un propósito";
                break;
            case 6:
                // Opcional
                break;
            default:
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const isSectionComplete = (sectionNumber) => {
        switch (sectionNumber) {
            case 1:
                return formData.projectName.trim() !== "";
            case 2:
                return formData.contactName.trim() !== "";
            case 3:
                return formData.location.trim() !== "";
            case 4:
                return formData.projectArea.trim() !== "";
            case 5:
                return formData.scanningPurpose.length > 0;
            case 6:
                return true;
            default:
                return false;
        }
    };

    const areAllRequiredSectionsComplete = () => {
        return (
            formData.projectName.trim() !== "" &&
            formData.contactName.trim() !== "" &&
            formData.location.trim() !== "" &&
            formData.projectArea.trim() !== "" &&
            formData.scanningPurpose.length > 0
        );
    };

    const handleContinueSection = (sectionNumber) => {
        if (validateSection(sectionNumber)) {
            if (!completedSections.includes(sectionNumber)) {
                setCompletedSections([...completedSections, sectionNumber]);
            }

            if (sectionNumber === 6) {
                setOpenSection(null);
            } else if (sectionNumber < 6) {
                setOpenSection(sectionNumber + 1);
            }
        }
    };

    const handleToggleSection = (sectionNumber) => {
        if (
            completedSections.includes(sectionNumber) ||
            sectionNumber === openSection ||
            sectionNumber === completedSections.length + 1
        ) {
            setOpenSection(
                openSection === sectionNumber ? null : sectionNumber
            );
        }
    };

    const canOpenSection = (sectionNumber) => {
        if (sectionNumber === 1) return true;
        return completedSections.includes(sectionNumber - 1);
    };

    const isCompleted = (sectionNumber) => {
        return completedSections.includes(sectionNumber);
    };

    const handleSubmit = () => {
        if (completedSections.length >= 5) {
            onNext(formData);
        }
    };

    return (
        <div className="step1">
            <div className="step1__container">
                <div className="accordion">
                    <div
                        className={`accordion-section ${
                            openSection === 1 ? "accordion-section--open" : ""
                        } ${
                            isCompleted(1) ? "accordion-section--completed" : ""
                        }`}
                    >
                        <button
                            type="button"
                            className="accordion-header"
                            onClick={() => handleToggleSection(1)}
                        >
                            <div className="accordion-header__left">
                                <div className="accordion-number">
                                    {isCompleted(1) ? <HiCheckCircle /> : "1"}
                                </div>
                                <div className="accordion-title">
                                    <h3>Nombre del Proyecto</h3>
                                    {isCompleted(1) && formData.projectName && (
                                        <span className="accordion-preview">
                                            {formData.projectName}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="accordion-header__right">
                                {openSection === 1 ? (
                                    <HiChevronUp />
                                ) : (
                                    <HiChevronDown />
                                )}
                            </div>
                        </button>

                        {openSection === 1 && (
                            <div className="accordion-content">
                                <div
                                    className={`form-field ${
                                        errors.projectName
                                            ? "form-field--error"
                                            : ""
                                    }`}
                                >
                                    <label htmlFor="projectName">
                                        Nombre del Proyecto *
                                    </label>
                                    <input
                                        type="text"
                                        id="projectName"
                                        name="projectName"
                                        value={formData.projectName}
                                        onChange={handleChange}
                                        placeholder="Ej: Escaneado Edificio Central"
                                        maxLength={50}
                                        autoFocus
                                    />
                                    {errors.projectName && (
                                        <span className="error-text">
                                            {errors.projectName}
                                        </span>
                                    )}
                                </div>

                                <Button
                                    variant="filled"
                                    icon={<HiArrowRight />}
                                    animationType="pop"
                                    onClick={() => handleContinueSection(1)}
                                    disabled={!isSectionComplete(1)}
                                >
                                    Continuar
                                </Button>
                            </div>
                        )}
                    </div>

                    <div
                        className={`accordion-section ${
                            openSection === 2 ? "accordion-section--open" : ""
                        } ${
                            isCompleted(2) ? "accordion-section--completed" : ""
                        } ${
                            !canOpenSection(2)
                                ? "accordion-section--locked"
                                : ""
                        }`}
                    >
                        <button
                            type="button"
                            className="accordion-header"
                            onClick={() => handleToggleSection(2)}
                            disabled={!canOpenSection(2)}
                        >
                            <div className="accordion-header__left">
                                <div className="accordion-number">
                                    {isCompleted(2) ? (
                                        <HiCheckCircle />
                                    ) : !canOpenSection(2) ? (
                                        <HiLockClosed />
                                    ) : (
                                        "2"
                                    )}
                                </div>
                                <div className="accordion-title">
                                    <h3>Información de Contacto</h3>
                                    {isCompleted(2) && formData.contactName && (
                                        <span className="accordion-preview">
                                            {formData.contactName}
                                            {formData.companyName &&
                                                ` - ${formData.companyName}`}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="accordion-header__right">
                                {!canOpenSection(2) ? (
                                    <HiLockClosed />
                                ) : openSection === 2 ? (
                                    <HiChevronUp />
                                ) : (
                                    <HiChevronDown />
                                )}
                            </div>
                        </button>

                        {openSection === 2 && (
                            <div className="accordion-content">
                                <div
                                    className={`form-field ${
                                        errors.contactName
                                            ? "form-field--error"
                                            : ""
                                    }`}
                                >
                                    <label htmlFor="contactName">
                                        Tu nombre *
                                    </label>
                                    <input
                                        type="text"
                                        id="contactName"
                                        name="contactName"
                                        value={formData.contactName}
                                        onChange={handleChange}
                                        placeholder="Nombre completo"
                                        autoFocus
                                    />
                                    {errors.contactName && (
                                        <span className="error-text">
                                            {errors.contactName}
                                        </span>
                                    )}
                                </div>

                                <div className="form-field">
                                    <label htmlFor="companyName">
                                        Nombre de la Empresa
                                    </label>
                                    <input
                                        type="text"
                                        id="companyName"
                                        name="companyName"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        placeholder="Tu empresa (opcional)"
                                    />
                                </div>

                                <div className="form-field">
                                    <label htmlFor="phone">Teléfono</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="+34 600 000 000"
                                    />
                                </div>

                                <Button
                                    variant="filled"
                                    icon={<HiArrowRight />}
                                    animationType="pop"
                                    onClick={() => handleContinueSection(2)}
                                    disabled={!isSectionComplete(2)}
                                >
                                    Continuar
                                </Button>
                            </div>
                        )}
                    </div>

                    <div
                        className={`accordion-section ${
                            openSection === 3 ? "accordion-section--open" : ""
                        } ${
                            isCompleted(3) ? "accordion-section--completed" : ""
                        } ${
                            !canOpenSection(3)
                                ? "accordion-section--locked"
                                : ""
                        }`}
                    >
                        <button
                            type="button"
                            className="accordion-header"
                            onClick={() => handleToggleSection(3)}
                            disabled={!canOpenSection(3)}
                        >
                            <div className="accordion-header__left">
                                <div className="accordion-number">
                                    {isCompleted(3) ? (
                                        <HiCheckCircle />
                                    ) : !canOpenSection(3) ? (
                                        <HiLockClosed />
                                    ) : (
                                        "3"
                                    )}
                                </div>
                                <div className="accordion-title">
                                    <h3>Ubicación del Proyecto</h3>
                                    {isCompleted(3) && formData.location && (
                                        <span className="accordion-preview">
                                            {formData.location}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="accordion-header__right">
                                {!canOpenSection(3) ? (
                                    <HiLockClosed />
                                ) : openSection === 3 ? (
                                    <HiChevronUp />
                                ) : (
                                    <HiChevronDown />
                                )}
                            </div>
                        </button>

                        {openSection === 3 && (
                            <div className="accordion-content">
                                <div
                                    className={`form-field ${
                                        errors.location
                                            ? "form-field--error"
                                            : ""
                                    }`}
                                >
                                    <label htmlFor="location">
                                        Dirección *
                                    </label>
                                    <div className="input-with-icon">
                                        <HiMapPin className="input-icon" />
                                        <input
                                            type="text"
                                            id="location"
                                            name="location"
                                            value={formData.location}
                                            onChange={handleLocationChange}
                                            placeholder="Escribe una dirección, ciudad o lugar..."
                                            autoFocus
                                        />
                                    </div>
                                    {isSearching && (
                                        <span className="searching-text">
                                            Buscando ubicación...
                                        </span>
                                    )}
                                    {errors.location && (
                                        <span className="error-text">
                                            {errors.location}
                                        </span>
                                    )}
                                </div>

                                <div className="map-wrapper">
                                    <label>
                                        Selecciona la ubicación en el mapa
                                    </label>
                                    <div className="map-container">
                                        <MapContainer
                                            center={mapPosition}
                                            zoom={13}
                                            style={{
                                                height: "100%",
                                                width: "100%",
                                            }}
                                            key={`${mapPosition[0]}-${mapPosition[1]}`}
                                        >
                                            <TileLayer
                                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            />
                                            <LocationMarker
                                                position={markerPosition}
                                                setPosition={handleMapClick}
                                            />
                                            <MapUpdater center={mapPosition} />
                                        </MapContainer>
                                    </div>
                                </div>

                                <Button
                                    variant="filled"
                                    icon={<HiArrowRight />}
                                    animationType="pop"
                                    onClick={() => handleContinueSection(3)}
                                    disabled={!isSectionComplete(3)}
                                >
                                    Continuar
                                </Button>
                            </div>
                        )}
                    </div>

                    <div
                        className={`accordion-section ${
                            openSection === 4 ? "accordion-section--open" : ""
                        } ${
                            isCompleted(4) ? "accordion-section--completed" : ""
                        } ${
                            !canOpenSection(4)
                                ? "accordion-section--locked"
                                : ""
                        }`}
                    >
                        <button
                            type="button"
                            className="accordion-header"
                            onClick={() => handleToggleSection(4)}
                            disabled={!canOpenSection(4)}
                        >
                            <div className="accordion-header__left">
                                <div className="accordion-number">
                                    {isCompleted(4) ? (
                                        <HiCheckCircle />
                                    ) : !canOpenSection(4) ? (
                                        <HiLockClosed />
                                    ) : (
                                        "4"
                                    )}
                                </div>
                                <div className="accordion-title">
                                    <h3>Área del Proyecto</h3>
                                    {isCompleted(4) && formData.projectArea && (
                                        <span className="accordion-preview">
                                            {formData.projectArea} m²
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="accordion-header__right">
                                {!canOpenSection(4) ? (
                                    <HiLockClosed />
                                ) : openSection === 4 ? (
                                    <HiChevronUp />
                                ) : (
                                    <HiChevronDown />
                                )}
                            </div>
                        </button>

                        {openSection === 4 && (
                            <div className="accordion-content">
                                <div
                                    className={`form-field ${
                                        errors.projectArea
                                            ? "form-field--error"
                                            : ""
                                    }`}
                                >
                                    <label htmlFor="projectArea">
                                        Área del Proyecto *
                                    </label>
                                    <div className="input-with-suffix">
                                        <input
                                            type="number"
                                            id="projectArea"
                                            name="projectArea"
                                            value={formData.projectArea}
                                            onChange={handleChange}
                                            placeholder="Ejemplo: 500"
                                            min="1"
                                            autoFocus
                                        />
                                        <span className="input-suffix">m²</span>
                                    </div>
                                    <span className="input-help">
                                        Introduce solo el número de metros
                                        cuadrados
                                    </span>
                                    {errors.projectArea && (
                                        <span className="error-text">
                                            {errors.projectArea}
                                        </span>
                                    )}
                                </div>

                                <Button
                                    variant="filled"
                                    icon={<HiArrowRight />}
                                    animationType="pop"
                                    onClick={() => handleContinueSection(4)}
                                    disabled={!isSectionComplete(4)}
                                >
                                    Continuar
                                </Button>
                            </div>
                        )}
                    </div>

                    <div
                        className={`accordion-section ${
                            openSection === 5 ? "accordion-section--open" : ""
                        } ${
                            isCompleted(5) ? "accordion-section--completed" : ""
                        } ${
                            !canOpenSection(5)
                                ? "accordion-section--locked"
                                : ""
                        }`}
                    >
                        <button
                            type="button"
                            className="accordion-header"
                            onClick={() => handleToggleSection(5)}
                            disabled={!canOpenSection(5)}
                        >
                            <div className="accordion-header__left">
                                <div className="accordion-number">
                                    {isCompleted(5) ? (
                                        <HiCheckCircle />
                                    ) : !canOpenSection(5) ? (
                                        <HiLockClosed />
                                    ) : (
                                        "5"
                                    )}
                                </div>
                                <div className="accordion-title">
                                    <h3>Propósito del Escaneo *</h3>
                                    {isCompleted(5) &&
                                        formData.scanningPurpose.length > 0 && (
                                            <span className="accordion-preview">
                                                {
                                                    formData.scanningPurpose
                                                        .length
                                                }{" "}
                                                seleccionado
                                                {formData.scanningPurpose
                                                    .length > 1
                                                    ? "s"
                                                    : ""}
                                            </span>
                                        )}
                                </div>
                            </div>
                            <div className="accordion-header__right">
                                {!canOpenSection(5) ? (
                                    <HiLockClosed />
                                ) : openSection === 5 ? (
                                    <HiChevronUp />
                                ) : (
                                    <HiChevronDown />
                                )}
                            </div>
                        </button>

                        {openSection === 5 && (
                            <div className="accordion-content">
                                <div
                                    className={`form-field ${
                                        errors.scanningPurpose
                                            ? "form-field--error"
                                            : ""
                                    }`}
                                >
                                    <p className="form-help-text">
                                        Selecciona uno o más
                                    </p>
                                    <div className="checkbox-grid">
                                        {scanningPurposes.map(
                                            (purpose, index) => (
                                                <label
                                                    key={index}
                                                    className="checkbox-item"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.scanningPurpose.includes(
                                                            purpose
                                                        )}
                                                        onChange={() =>
                                                            handleCheckboxChange(
                                                                purpose
                                                            )
                                                        }
                                                    />
                                                    <span>{purpose}</span>
                                                </label>
                                            )
                                        )}
                                    </div>
                                    {errors.scanningPurpose && (
                                        <span className="error-text">
                                            {errors.scanningPurpose}
                                        </span>
                                    )}

                                    <input
                                        type="text"
                                        name="otherPurpose"
                                        value={formData.otherPurpose}
                                        onChange={handleChange}
                                        placeholder="Otro (especificar)"
                                        style={{ marginTop: "12px" }}
                                    />
                                </div>

                                <Button
                                    variant="filled"
                                    icon={<HiArrowRight />}
                                    animationType="pop"
                                    onClick={() => handleContinueSection(5)}
                                    disabled={!isSectionComplete(5)}
                                >
                                    Continuar
                                </Button>
                            </div>
                        )}
                    </div>

                    <div
                        className={`accordion-section ${
                            openSection === 6 ? "accordion-section--open" : ""
                        } ${
                            isCompleted(6) ? "accordion-section--completed" : ""
                        } ${
                            !canOpenSection(6)
                                ? "accordion-section--locked"
                                : ""
                        }`}
                    >
                        <button
                            type="button"
                            className="accordion-header"
                            onClick={() => handleToggleSection(6)}
                            disabled={!canOpenSection(6)}
                        >
                            <div className="accordion-header__left">
                                <div className="accordion-number">
                                    {isCompleted(6) ? (
                                        <HiCheckCircle />
                                    ) : !canOpenSection(6) ? (
                                        <HiLockClosed />
                                    ) : (
                                        "6"
                                    )}
                                </div>
                                <div className="accordion-title">
                                    <h3>Alcance del Trabajo</h3>
                                    <span className="accordion-badge">
                                        Opcional
                                    </span>
                                </div>
                            </div>
                            <div className="accordion-header__right">
                                {!canOpenSection(6) ? (
                                    <HiLockClosed />
                                ) : openSection === 6 ? (
                                    <HiChevronUp />
                                ) : (
                                    <HiChevronDown />
                                )}
                            </div>
                        </button>

                        {openSection === 6 && (
                            <div className="accordion-content">
                                <div className="form-field">
                                    <label htmlFor="scopeOfWork">
                                        Descripción del proyecto
                                    </label>
                                    <textarea
                                        id="scopeOfWork"
                                        name="scopeOfWork"
                                        value={formData.scopeOfWork}
                                        onChange={handleChange}
                                        placeholder="Número de niveles, fase de construcción, detalles de demolición, inclusiones y exclusiones específicas, etc."
                                        rows="6"
                                        autoFocus
                                    />
                                </div>

                                <Button
                                    variant="filled"
                                    icon={<HiCheckCircle />}
                                    animationType="pop"
                                    onClick={() => handleContinueSection(6)}
                                    disabled={false}
                                >
                                    Finalizar esta sección
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-actions">
                    <Button
                        variant="outline"
                        icon={<HiArrowLeft />}
                        animationType="pop"
                        onClick={onBack}
                    >
                        Volver
                    </Button>

                    <Button
                        variant="filled"
                        icon={<HiArrowRight />}
                        animationType="pop"
                        onClick={handleSubmit}
                        disabled={!areAllRequiredSectionsComplete()}
                    >
                        Continuar al Paso 2
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Step1;
